type CacheItemKey = number | string;
type CacheMap<T> = Map<CacheItemKey, T>;

class CacheWithLimit<T> {
  private readonly maxSize: number;
  private cache: CacheMap<T>;

  /** Item at index 0 is first item to be deleted when maxSize is surpassed */
  private deletionPriorityQueue: CacheItemKey[];

  /** Creates a cache to store items up to a maximum size
   * If a new item is added and cache is at max size, remove oldest item.
   */
  constructor(maxSize: number, initMap: CacheMap<T> = new Map()) {
    this.maxSize = maxSize;
    this.cache =
      initMap.size > maxSize
        ? new Map([...initMap.entries()].slice(0, maxSize))
        : initMap;
    this.deletionPriorityQueue = [...this.cache.keys()];
  }

  get size() {
    return this.cache.size;
  }

  set(itemKey: CacheItemKey, data: T) {
    // If item with itemKey already exists, remove it and replace
    const itemKeyExists = this.cache.has(itemKey);
    if (itemKeyExists) {
      this.cache.delete(itemKey);
      this.cache.set(itemKey, data);
      this._deprioritizeDeletion(itemKey);
    } else {
      if (this.cache.size === this.maxSize) this._deleteLowestPriority();
      this.cache.set(itemKey, data);
    }
  }

  get(itemKey: CacheItemKey): T | null {
    const data = this.cache.get(itemKey);
    if (!data) return null;
    this._deprioritizeDeletion(itemKey);
    return data;
  }

  clear() {
    this.cache.clear();
    this.deletionPriorityQueue = [];
  }

  _deprioritizeDeletion(itemKey: CacheItemKey) {
    const queueIndex = this.deletionPriorityQueue.indexOf(itemKey);
    if (queueIndex === -1) return;
    this.deletionPriorityQueue.splice(queueIndex, 1);
    this.deletionPriorityQueue.push(itemKey);
  }

  _deleteLowestPriority() {
    const itemKey = this.deletionPriorityQueue[0];
    this.deletionPriorityQueue.splice(0, 1);
    this.cache.delete(itemKey);
  }
}

export { CacheWithLimit };
